import { useCallback, useEffect, useState } from 'react';
import { avisar, avisarErro } from '../componentes/Avisos.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { FormularioProspeccao, type DadosDaProspeccao } from '../componentes/FormularioProspeccao.tsx';
import {
  ErroDaApi,
  apiComercial,
  type OrdemDoFunil,
  type PainelComercial,
  type Prospeccao,
  type SituacaoDaProspeccao,
} from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';
import { ETAPAS, GRUPOS, MOTIVO_DO_INSUCESSO, nomeDaEtapa, quandoFoi, quandoVence } from '../lib/funil.ts';
import '../estilos/sistema.css';
import '../estilos/comercial.css';

/**
 * O próximo passo na linha da lista: o que vem, e quando.
 *
 * ⚠️ **Vermelho só quando passou do dia.** Pintar tudo de vermelho faria o
 * olho parar de distinguir o que está atrasado do que está em dia — que é a
 * única distinção que importa aqui.
 */
function ProximoNaLinha({ p }: { p: Prospeccao }) {
  const vence = quandoVence(p.proximoPassoEm);
  return (
    <small className={`com-proximo${vence?.atrasado ? ' atrasado' : ''}`}>
      {p.proximoPasso}
      {vence && <> · {vence.texto}</>}
    </small>
  );
}


/**
 * ⭐⭐ A ABA COMERCIAL — a lista, 20/09/2026 (PRD §49 e §49.10).
 *
 * > *"Precisa ter um botão para aplicar a ordem da lista… ordenar por data de
 * > último contato, por data de cadastro, ordem alfabética."* — Richard
 *
 * ⭐ **Virou TABELA, e não foi só estética.** Cartão empilhado serve para
 * escolher entre poucos; tabela serve para **comparar e ordenar**, que é o que
 * 120 empresas pedem. Cada coluna que decide alguma coisa é um botão de ordem,
 * e há o seletor explícito no alto — porque descobrir que o cabeçalho é
 * clicável exige tentar, e ele pediu um botão.
 *
 * ⛔ **A numeração da fila SAIU da lista** (*"acho que essa numeração de ordem
 * não é necessária"*). ⚠️ O campo continua no banco, com o valor que tinha: a
 * ordem do levantamento agora se lê ordenando por **cadastro**, que devolve
 * exatamente a sequência em que as 120 foram priorizadas.
 */

const ORDENS: Array<{ id: OrdemDoFunil; nome: string; crescente: string; decrescente: string }> = [
  { id: 'cadastro', nome: 'Data de cadastro', crescente: 'a primeira da pesquisa', decrescente: 'a última cadastrada' },
  { id: 'nome', nome: 'Nome da empresa', crescente: 'de A a Z', decrescente: 'de Z a A' },
  { id: 'contato', nome: 'Último contato', crescente: 'nunca contatadas primeiro', decrescente: 'as mais recentes' },
  { id: 'movimentacao', nome: 'Última movimentação', crescente: 'as mais paradas primeiro', decrescente: 'as que andaram agora' },
  { id: 'etapa', nome: 'Etapa do funil', crescente: 'do começo ao fim', decrescente: 'do fim ao começo' },
  { id: 'cidade', nome: 'Cidade e UF', crescente: 'de A a Z', decrescente: 'de Z a A' },
  /* ⭐ A fila do dia na sequência em que se trabalha — 30/09/2026. */
  { id: 'passo', nome: 'Próximo passo', crescente: 'o mais próximo primeiro', decrescente: 'o mais distante primeiro' },
];

export function Comercial({
  ehMaster,
  aoAbrirEmpresa,
  aoExpirarSessao,
}: {
  ehMaster: boolean;
  aoAbrirEmpresa: (id: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [painel, setPainel] = useState<PainelComercial | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [situacao, setSituacao] = useState('');
  /** ⛔ A vista de quem pediu para não ser procurada. Fora da fila, não do Hub. */
  const [vendoQuemPediu, setVendoQuemPediu] = useState(false);
  /** ⭐ A fila do dia: '' (tudo), 'hoje' ou 'atrasado'. */
  const [filaDoDia, setFilaDoDia] = useState('');
  const [grupo, setGrupo] = useState('');
  /** ⭐ Só as empresas com e-mail que voltou — 29/09/2026, pedido dele. */
  const [vendoVoltaram, setVendoVoltaram] = useState(false);
  /** ⭐ PRD §63.2: só as do "insucesso" (ficaram sem e-mail que receba). */
  const [vendoInsucesso, setVendoInsucesso] = useState(false);
  const [termo, setTermo] = useState('');
  const [ordem, setOrdem] = useState<OrdemDoFunil>('cadastro');
  const [sentido, setSentido] = useState<'asc' | 'desc'>('asc');
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setPainel(
        await apiComercial.listar({
          situacao,
          grupo,
          termo,
          ordem,
          sentido,
          ...(vendoQuemPediu ? { naoContatar: '1' } : {}),
          ...(vendoVoltaram ? { voltaram: '1' } : {}),
          ...(vendoInsucesso ? { insucesso: '1' } : {}),
          ...(filaDoDia ? { fila: filaDoDia } : {}),
        }),
      );
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro('Não consegui carregar o funil.');
    } finally {
      setCarregando(false);
    }
  }, [situacao, grupo, termo, ordem, sentido, vendoQuemPediu, vendoVoltaram, vendoInsucesso, filaDoDia, aoExpirarSessao]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), termo ? 320 : 0);
    return () => clearTimeout(t);
  }, [carregar, termo]);

  const lista = painel?.prospeccoes ?? [];
  const escolhida = ORDENS.find((o) => o.id === ordem)!;

  /*
   * ⛔⛔ **O NÚMERO NO BOTÃO É O QUE O CLIQUE MOSTRA — 29 e 30/09/2026.** A
   * fila do dia conta DENTRO da etapa marcada (o servidor), e por isso etapa e
   * fila se combinam: "A contatar" com "para hoje" são os e-mails do dia. Os
   * e-mails que voltaram contam endereços do funil inteiro, e por isso esse
   * botão solta os outros.
   */
  function soAEtapa(id: string) {
    setSituacao(id);
    setVendoVoltaram(false);
    setVendoInsucesso(false);
  }
  function soAFila(f: string) {
    setFilaDoDia(f);
    if (f) setVendoVoltaram(false);
    /* ⭐ Abrindo a fila, a lista vem na ordem em que se trabalha. */
    if (f && ordem === 'cadastro') {
      setOrdem('passo');
      setSentido('asc');
    }
  }
  function soOsQueVoltaram(v: boolean) {
    setVendoVoltaram(v);
    if (v) {
      setSituacao('');
      setFilaDoDia('');
      setVendoInsucesso(false);
    }
  }
  /* ⭐ PRD §63.2: o "insucesso" também solta os outros filtros: conta o funil inteiro. */
  function soOInsucesso(v: boolean) {
    setVendoInsucesso(v);
    if (v) {
      setSituacao('');
      setFilaDoDia('');
      setVendoVoltaram(false);
    }
  }

  /**
   * ⚠️ **Clicar na coluna que já ordena INVERTE o sentido**, e não reordena do
   * zero — é o que todo mundo espera de uma tabela. Coluna nova começa
   * crescente, que é a leitura natural de cada uma delas.
   */
  function ordenarPor(id: OrdemDoFunil) {
    if (id === ordem) return setSentido((s) => (s === 'asc' ? 'desc' : 'asc'));
    setOrdem(id);
    setSentido('asc');
  }

  const Coluna = ({ id, children }: { id: OrdemDoFunil; children: React.ReactNode }) => (
    <th scope="col" aria-sort={ordem === id ? (sentido === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`com-col${ordem === id ? ' ativa' : ''}`} onClick={() => ordenarPor(id)}>
        {children}
        <Icone nome={ordem === id && sentido === 'desc' ? 'subir' : 'avancar'} tamanho={12} />
      </button>
    </th>
  );

  return (
    <div className="com">
      <header className="com-topo">
        <div>
          <h1>Comercial</h1>
          <p className="com-linha">
            O funil, do primeiro contato à proposta assinada. {painel ? `${painel.total} empresa(s) nesta vista.` : ''}
          </p>
        </div>
        <div className="com-acoes">
          <label className="com-busca">
            <Icone nome="lupa" />
            <input
              type="search"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder="Empresa, decisor ou cidade"
              aria-label="Procurar no funil"
            />
          </label>
          {/* ⚠️ Cadastrar é do colaborador também: o trabalho do dia traz empresa nova. */}
          <button type="button" className="botao-primario" onClick={() => setCriando(true)}>
            <Icone nome="mais" tamanho={14} /> Nova empresa
          </button>
        </div>
      </header>

      {/* O placar: quantos em cada etapa, e o clique filtra. */}
      <div className="com-placar" role="tablist" aria-label="Etapas do funil">
        <button
          type="button"
          role="tab"
          aria-selected={situacao === '' && !vendoVoltaram && !vendoQuemPediu && !vendoInsucesso}
          className={situacao === '' && !vendoVoltaram && !vendoQuemPediu && !vendoInsucesso ? 'ativa' : undefined}
          onClick={() => {
            soAEtapa('');
            setVendoQuemPediu(false);
          }}
        >
          <b>{Object.values(painel?.placar ?? {}).reduce((a, b) => a + b, 0)}</b>
          <span>Todas</span>
        </button>
        {ETAPAS.map((e) => (
          <button
            key={e.id}
            type="button"
            role="tab"
            aria-selected={situacao === e.id}
            title={e.ajuda}
            className={`${situacao === e.id ? 'ativa ' : ''}etapa-${e.id}`}
            onClick={() => soAEtapa(situacao === e.id ? '' : e.id)}
          >
            <b>{painel?.placar[e.id] ?? 0}</b>
            <span>{e.nome}</span>
          </button>
        ))}
        {/*
          ⛔⛔ **Fora do placar, e é de propósito: não é etapa.** Uma empresa
          pode estar conversando e pedir para sair — o pedido é ortogonal ao
          estágio comercial, e somá-lo às etapas faria o funil contar duas
          vezes a mesma empresa.

          ⚠️ **Só aparece quando existe.** Botão que mostra zero para sempre
          vira paisagem, e no dia em que houver um ninguém repara.
        */}
        {!!painel?.naoContatar && (
          <button
            type="button"
            role="tab"
            aria-selected={vendoQuemPediu}
            title="Pediram para não ser procuradas. Estão fora da fila."
            className={`com-fora-da-fila${vendoQuemPediu ? ' ativa' : ''}`}
            onClick={() => setVendoQuemPediu(!vendoQuemPediu)}
          >
            <b>{painel.naoContatar}</b>
            <span>Não contatar</span>
          </button>
        )}

        {/*
          ⭐⭐ **QUANTOS E-MAILS VOLTARAM — 23/09/2026.**

          ⚠️ **Não é etapa**: é a única medida de qualidade que este volume dá.
          `docs/PROSPECCAO.md` §4.7 — com dez por dia não há relatório de spam
          que fale, e o que volta, volta em minutos.

          ⭐ **Virou botão em 29/09/2026, pedido dele:** o clique mostra as
          empresas com endereço devolvido, para corrigir na ficha de cada uma.

          ⚠️ **Conta ENDEREÇOS, não empresas.** Uma empresa com três contatos e
          um endereço morto é um endereço errado, não uma empresa errada.

          ⛔ **Só aparece quando existe**, como os outros.
        */}
        {!!painel?.emailsQueVoltaram && (
          <button
            type="button"
            role="tab"
            aria-selected={vendoVoltaram}
            className={`com-voltaram${vendoVoltaram ? ' ativa' : ''}`}
            title="Endereços que o servidor devolveu. O clique mostra as empresas; a correção é na ficha de cada uma."
            onClick={() => soOsQueVoltaram(!vendoVoltaram)}
          >
            <b>{painel.emailsQueVoltaram}</b>
            <span>{painel.emailsQueVoltaram === 1 ? 'e-mail voltou' : 'e-mails voltaram'}</span>
          </button>
        )}

        {/*
          ⭐⭐ **O INSUCESSO — PRD §63.2, 22/10/2026.** A empresa ficou sem e-mail que receba (todo
          endereço voltou, o domínio não recebe ou recusa os nossos e-mails) e saiu da fila. ⚠️ Conta
          EMPRESAS, ao contrário do botão ao lado. ⛔ Só aparece quando existe, como os outros.
        */}
        {!!painel?.insucessos && (
          <button
            type="button"
            role="tab"
            aria-selected={vendoInsucesso}
            className={`com-insucesso${vendoInsucesso ? ' ativa' : ''}`}
            title={`Ficaram sem e-mail que receba e saíram da fila.${painel.paraPesquisar ? ` ${painel.paraPesquisar} esperando a pesquisa de outro e-mail.` : ''}`}
            onClick={() => soOInsucesso(!vendoInsucesso)}
          >
            <b>{painel.insucessos}</b>
            <span>insucesso</span>
          </button>
        )}
      </div>

      {/*
        ⭐⭐ **A FILA DO DIA — 21/09/2026.** Vem antes dos filtros porque é o
        que a pessoa abre a aba para ver: o que fazer hoje.

        ⚠️ **"Para hoje" INCLUI o atrasado**, e a segunda linha diz quanto
        disso é dívida. Somar os dois faria a mesma empresa ser contada duas
        vezes; separar sem dizer faria parecer que há mais trabalho do que há.

        ⛔ **Só aparece quando existe.** Faixa que mostra zero todo dia vira
        paisagem, e no dia em que houver trabalho ninguém repara.
      */}
      {(!!painel?.fila.paraHoje || !!filaDoDia) && !vendoQuemPediu && painel && (
        <div className="com-fila">
          <button
            type="button"
            className={`com-fila-bt${filaDoDia === 'hoje' ? ' ativa' : ''}`}
            onClick={() => soAFila(filaDoDia === 'hoje' ? '' : 'hoje')}
          >
            <b>{painel.fila.paraHoje}</b>
            <span>para hoje</span>
          </button>
          {(!!painel.fila.atrasado || filaDoDia === 'atrasado') && (
            <button
              type="button"
              className={`com-fila-bt atrasado${filaDoDia === 'atrasado' ? ' ativa' : ''}`}
              onClick={() => soAFila(filaDoDia === 'atrasado' ? '' : 'atrasado')}
            >
              <b>{painel.fila.atrasado}</b>
              <span>passou do dia</span>
            </button>
          )}
          <p className="com-fila-nota">
            O que está marcado para hoje ou antes. <strong>Passou do dia</strong> já está incluído em{' '}
            <strong>para hoje</strong>.
            {situacao && (
              <>
                {' '}Contando só <strong>{nomeDaEtapa(situacao as never)}</strong>.
              </>
            )}
          </p>
        </div>
      )}

      {vendoQuemPediu && (
        <p className="com-aviso-fora">
          <Icone nome="atencao" tamanho={14} />
          <span>
            Estas empresas <strong>pediram para não ser procuradas</strong>. Elas ficam na base para que não
            voltem à fila por engano, e o Hub recusa registrar contato novo com elas.
          </span>
        </p>
      )}

      <div className="com-filtros">
        <label>
          <span className="rotulo">Grupo</span>
          <select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="">todos</option>
            {Object.entries(GRUPOS).map(([id, nome]) => (
              <option key={id} value={id}>{nome}</option>
            ))}
          </select>
        </label>
        {/* ⛔ O filtro "Modelo de texto" saiu em 29/09/2026: o e-mail de
            apresentação é um só para todas as empresas, e a letra que o
            levantamento de 20/09 deu a cada uma não escolhe mais nada. */}

        {/* ⭐ O botão de ordem que ele pediu. O cabeçalho da tabela faz o mesmo,
            e os dois andam juntos — quem clica na coluna vê mudar aqui. */}
        <label className="com-ordenar">
          <span className="rotulo">Ordenar por</span>
          <select value={ordem} onChange={(e) => ordenarPor(e.target.value as OrdemDoFunil)}>
            {ORDENS.map((o) => (
              <option key={o.id} value={o.id}>{o.nome}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="com-sentido"
          onClick={() => setSentido((s) => (s === 'asc' ? 'desc' : 'asc'))}
          title={sentido === 'asc' ? escolhida.crescente : escolhida.decrescente}
        >
          <Icone nome={sentido === 'asc' ? 'avancar' : 'subir'} tamanho={13} />
          {sentido === 'asc' ? escolhida.crescente : escolhida.decrescente}
        </button>
      </div>

      {carregando && !painel ? (
        <p className="com-vazio">Carregando…</p>
      ) : lista.length === 0 ? (
        <p className="com-vazio">Nenhuma empresa nesta vista.</p>
      ) : (
        <div className="com-tabela-fora">
          <table className="com-tabela">
            <thead>
              <tr>
                <Coluna id="nome">Empresa</Coluna>
                <th scope="col">Quem decide</th>
                <Coluna id="cidade">Onde</Coluna>
                <Coluna id="etapa">Etapa</Coluna>
                <Coluna id="contato">Último contato</Coluna>
                <Coluna id="cadastro">Cadastro</Coluna>
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => {
                const principal = p.contatos.find((c) => c.principal) ?? p.contatos[0];
                return (
                  <tr key={p.id} onClick={() => aoAbrirEmpresa(p.id)} tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        aoAbrirEmpresa(p.id);
                      }
                    }}
                  >
                    <th scope="row">
                      <span className="com-nome-cel">{p.nome}</span>
                      {p.perfil && <small>{p.perfil}</small>}
                      {/* ⭐ PRD §63.2: a marca do insucesso, com o motivo, na própria linha. */}
                      {p.insucessoEm && (
                        <span className="com-insucesso-marca">
                          insucesso: {MOTIVO_DO_INSUCESSO[p.insucessoMotivo ?? ''] ?? 'sem e-mail que receba'}
                        </span>
                      )}
                      {/*
                        ⭐ **O próximo passo vem embaixo do nome**, e não em
                        coluna própria: é informação de leitura, não de
                        comparação — quem varre a lista procura a empresa, e o
                        que vem é o detalhe dela.
                      */}
                      {p.proximoPasso && <ProximoNaLinha p={p} />}
                    </th>
                    <td>
                      {principal?.nome ? (
                        <>
                          <span className="com-decisor-cel">{principal.nome}</span>
                          {principal.cargo && <small>{principal.cargo}</small>}
                        </>
                      ) : (
                        <i className="com-fraco">sem decisor publicado</i>
                      )}
                      {p.contatos.length > 1 && <span className="com-mais">+{p.contatos.length - 1}</span>}
                    </td>
                    <td className="com-onde">
                      {p.cidade ? `${p.cidade}${p.uf ? `/${p.uf}` : ''}` : <i className="com-fraco">—</i>}
                      <small>{GRUPOS[p.grupo] ?? p.grupo}</small>
                    </td>
                    <td><span className={`com-etapa etapa-${p.situacao}`}>{nomeDaEtapa(p.situacao)}</span></td>
                    {/* ⚠️ "nunca" em itálico fraco, e não um traço: a diferença
                        entre nunca contatada e contatada há muito tempo é a
                        própria fila de trabalho. */}
                    <td className="com-quando-cel">
                      {quandoFoi(p.ultimoContatoEm) ?? <i className="com-fraco">nunca</i>}
                    </td>
                    <td className="com-quando-cel dado">{paraTela(p.criadoEm.slice(0, 10))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {criando && (
        <FormularioProspeccao
          inicial={null}
          aoFechar={() => setCriando(false)}
          aoSalvar={async (dados: DadosDaProspeccao) => {
            const nova = await apiComercial.criar(dados);
            setCriando(false);
            await carregar();
            if (nova.avisoDoDocumento) avisar(nova.avisoDoDocumento, 'ruim');
            aoAbrirEmpresa(nova.id);
          }}
        />
      )}
    </div>
  );
}
